/** Reconcile display snapshots; never resolve or advance backend business state. */
const retain = (accepted) => accepted;
const replace = (_accepted, incoming) => incoming;
const SOURCE_PRIORITY = new Map([['stream', 0], ['history', 1]]);
const SOURCE_NAMES = new Map([[true, 'history'], [false, 'stream']]);
const SOURCE_TIE_POLICY = new Map([[-1, retain], [0, retain], [1, replace]]);
const preferCommitted = (accepted, incoming) => SOURCE_TIE_POLICY.get(Math.sign(
  SOURCE_PRIORITY.get(incoming.source) - SOURCE_PRIORITY.get(accepted.source),
))(accepted, incoming);
const SNAPSHOT_POLICY = new Map([[-1, retain], [0, preferCommitted], [1, replace]]);

const sequence = (value) => Number.isSafeInteger(value) && value >= 0 ? value : null;
const text = (value) => typeof value === 'string' ? value.trim() : '';

/** @returns {import('./contracts').ProcessIdentity | null} */
export const processIdentity = (sessionId, runId, requestId) => {
  const values = [sessionId, runId, requestId].map(text);
  if (!values.every(Boolean)) return null;
  return { sessionId: values[0], runId: values[1], requestId: values[2] };
};

/** Only Run event sequences are comparable; transport/loading counters are not. */
export const processProjectionSequence = (projection) => sequence(
  projection?.last_event_sequence ?? projection?.progress_projection?.freshness?.last_event_sequence,
);

/** @param {import('./contracts').ProcessIdentity | null} identity */
const identityKey = (identity) => {
  if (!identity) return null;
  const normalized = processIdentity(identity.sessionId, identity.runId, identity.requestId);
  if (!normalized) return null;
  return JSON.stringify([normalized.sessionId, normalized.runId, normalized.requestId]);
};

/**
 * Newer accepted sequence wins; equal/absent sequences retain the persisted snapshot.
 * @template T
 * @param {import('./contracts').ProcessSnapshot<T> | null} accepted
 * @param {import('./contracts').ProcessSnapshot<T>} incoming
 */
const selectSnapshot = (accepted, incoming) => {
  if (!accepted) return incoming;
  const order = Math.sign((incoming.sequence ?? -1) - (accepted.sequence ?? -1));
  return SNAPSHOT_POLICY.get(order)(accepted, incoming);
};

const sourceOf = (item) => SOURCE_NAMES.get(item.historical === true);
/** @param {import('./contracts').ProcessProjectionMetadata & {historical?: boolean}} item */
const snapshotOf = (item) => ({ value: item, sequence: sequence(item.projectionSequence), source: sourceOf(item) });

/** @param {import('./contracts').ProcessActivityIdentity} activity */
const activityKey = (activity, identity) => {
  const scope = processIdentity(activity?.session_id, activity?.run_id, activity?.request_id);
  if (!scope || identityKey(scope) !== identityKey(identity)) return null;
  if (!text(activity.kind) || !text(activity.activity_id)) return null;
  return JSON.stringify([identityKey(scope), activity.kind, activity.activity_id]);
};

const activitySnapshots = (item) => (item.processPhases ?? []).map((value) => ({
  value, sequence: sequence(value?.sequence), source: sourceOf(item),
}));

const collectActivity = (accepted, incoming, identity) => {
  // Incomplete legacy identities are retained separately, not guessed into one activity.
  const key = activityKey(incoming.value, identity) ?? Symbol('unbound-activity');
  accepted.set(key, selectSnapshot(accepted.get(key), incoming));
  return accepted;
};

const mergeActivities = (items, identity) => Array.from(items.flatMap(activitySnapshots).reduce(
  (accepted, incoming) => collectActivity(accepted, incoming, identity), new Map(),
).values()).sort((left, right) => (left.sequence ?? -1) - (right.sequence ?? -1)).map(({ value }) => value);

const resolvedItem = (group) => {
  const selected = group.items.map(snapshotOf).reduce(selectSnapshot, null).value;
  const processPhases = mergeActivities(group.items, selected.processIdentity);
  return { ...selected, processPhases, phase: processPhases.at(-1) ?? selected.phase };
};

const collectGroup = (groups, item, index) => {
  if (item.type !== 'thinking') return groups;
  const key = identityKey(item.processIdentity);
  if (!key) return groups;
  const group = groups.get(key) ?? { firstIndex: index, items: [] };
  groups.set(key, { ...group, items: [...group.items, item] });
  return groups;
};

const projectItem = (item, index, groups) => {
  if (item.type !== 'thinking') return item;
  const group = groups.get(identityKey(item.processIdentity));
  if (!group) return item;
  return new Map([[true, resolvedItem], [false, () => null]]).get(group.firstIndex === index)(group);
};

/** Preserve all message/asset items and the original process position. No mutable store or IO. */
export const reconcileProcessItems = (items) => {
  const groups = items.reduce(collectGroup, new Map());
  return items.map((item, index) => projectItem(item, index, groups)).filter(Boolean);
};
